import { MenuItem } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { FeatureFlagIdReviewsPendingActions } from 'c1gen/features'
import { C1Link } from 'components/core/links/C1Link'
import { useFeatureChecker } from 'components/no-ui/capability'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { routes } from 'lib/routes'
import { RemovePendingActionRequest } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { TaskActionsServiceCloseRequest } from 'pbts/c1/api/task/v1/actions'
import { FC, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { removePendingActionApi } from 'redux_toolkit/access_review/pending_actions'
import { cancelTaskApi } from 'redux_toolkit/tasks'

type CancelTaskDropdownOptionProps = {
    task: TaskValue
    close: () => void
}

export const CancelTaskDropdownOption: FC<CancelTaskDropdownOptionProps> = ({ task, close }) => {
    const toast = useAlert()
    const { mutate: cancelTask } = cancelTaskApi()
    const { mutate: removePendingAction } = removePendingActionApi()
    const hasPendingActionsFeature = useFeatureChecker(FeatureFlagIdReviewsPendingActions) ?? false

    const handleClick = useCallback(async (): Promise<void> => {
        close()

        try {
            // Cancel the task first
            await cancelTask(
                TaskActionsServiceCloseRequest.create({
                    taskId: task.id,
                    comment: '',
                    expandMask: { paths: ['*'] },
                }),
            )

            toast(
                <FormattedMessage
                    id="task_cancel_success"
                    defaultMessage="Task {task_link} canceled successfully"
                    values={{
                        task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                    }}
                />,
                'success',
            )

            // Only remove pending action after successful cancellation
            if (hasPendingActionsFeature && task.accessReview?.id) {
                try {
                    await removePendingAction(
                        RemovePendingActionRequest.create({
                            accessReviewId: task.accessReview.id,
                            certifyTaskId: task.id,
                        }),
                    )
                } catch (err) {
                    // Log error but don't block - task is already cancelled
                    console.error('Failed to remove pending action:', err)
                }
            }
        } catch (err) {
            toast(
                <FormattedMessage
                    id="task_cancel_error"
                    defaultMessage="Error cancelling task {task_link}. {code}: {msg}"
                    values={{
                        task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }
    }, [cancelTask, close, task.id, task.numericId, task.accessReview?.id, toast, removePendingAction, hasPendingActionsFeature])

    return (
        <MenuItem onClick={handleClick} key={`component_task_list_cancel_option_label_${task.id}`}>
            <FormattedMessage id="component_task_list_cancel_option_label" defaultMessage="Cancel" />
        </MenuItem>
    )
}
